import React, { useRef } from 'react';
import { Search, RefreshCw, X, AlertCircle } from 'lucide-react';
import { ScanTextInput } from '../../../common/inputs';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { UseFreeReceivingResult } from './hooks/useFreeReceiving';

interface FreeReceivingScanPanelProps {
  /** Delt hook-instans fra POrderReceivingDetail (receivingMode='free'). */
  free: UseFreeReceivingResult;
  /** Global strekkodeskanner aktivert (systeminnstilling). */
  barcodeScannerEnabled: boolean;
  /** Skjul søkefeltet (f.eks. mens en linje redigeres). */
  hidden?: boolean;
}

/**
 * FreeReceivingScanPanel — ren fri-modus-UI for varemottak: søkefelt (skann vilkårlig vare på
 * tvers av åpne innkjøpsordrer) + velgmodal ved flere treff. All logikk ligger i useFreeReceiving;
 * denne komponenten rendrer kun state og kaller handlingene. Porteres fra tidligere OpenReceiving.
 */
export const FreeReceivingScanPanel: React.FC<FreeReceivingScanPanelProps> = ({
  free,
  barcodeScannerEnabled,
  hidden = false,
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = free.searchInput.trim();
    if (value) {
      free.handleScan(value);
      free.setSearchInput('');
    }
  };

  return (
    <>
      {/* Not-found / feilmelding */}
      {free.notFoundItemNo && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {t('warehouse.modules.consolidatedReceiving.noOpenPOLinesFound', { itemNo: free.notFoundItemNo }, `Ingen åpne innkjøpslinjer funnet for ${free.notFoundItemNo}`)}
          </div>
          <button onClick={free.clearSearchError} className="text-red-700 hover:text-red-900">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      {free.searchError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {t('warehouse.modules.consolidatedReceiving.searchError', { error: free.searchError }, `Søkefeil: ${free.searchError}`)}
          </div>
          <button onClick={free.clearSearchError} className="text-red-700 hover:text-red-900">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Søkefelt */}
      {!hidden && (
        <div className="bg-white rounded-lg shadow p-4">
          <form onSubmit={handleSubmit} className="flex space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              <ScanTextInput
                ref={searchInputRef}
                value={free.searchInput}
                onChange={(e) => free.setSearchInput(e.target.value)}
                placeholder={t('warehouse.modules.receipt.placeholders.scanBarcode', {}, 'Skann eller skriv varenr/strekkode...')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={free.isSearching}
              />
            </div>
            <button
              type="submit"
              disabled={free.isSearching || !free.searchInput.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {free.isSearching ? (
                <RefreshCw className="h-5 w-5 animate-spin" />
              ) : (
                t('common.buttons.search', {}, 'Søk')
              )}
            </button>
          </form>

          {/* Mobil strekkode-fangst */}
          <MobileBarcodeCaptureInput
            onScan={(barcode) => free.handleScan(barcode)}
            enabled={barcodeScannerEnabled && !free.isSearching && !free.showLineSelector}
          />
        </div>
      )}

      {/* Velgmodal ved flere treff */}
      {free.showLineSelector && free.matchingLines.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                {t('warehouse.modules.consolidatedReceiving.selectPOLine', { count: free.matchingLines.length }, `Velg innkjøpslinje (${free.matchingLines.length} treff)`)}
              </h3>
              <button
                onClick={free.closeLineSelector}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {free.matchingLines.map((line) => (
                <button
                  key={`${line.orderNo}-${line.lineNo}`}
                  onClick={() => free.chooseLine(line)}
                  className="w-full px-4 py-3 border-b border-gray-100 hover:bg-blue-50 text-left transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900">
                        {line.itemNo} - {line.description1}
                      </div>
                      <div className="text-sm text-gray-500">
                        {t('warehouse.modules.consolidatedReceiving.order', {}, 'Ordre')}: {line.orderNoERP || line.orderNo} | {t('warehouse.modules.consolidatedReceiving.supplier', {}, 'Leverandør')}: {line.supplierName || line.supplierNo}
                      </div>
                      <div className="text-xs text-gray-400">
                        {t('warehouse.modules.consolidatedReceiving.ordered', {}, 'Bestilt')}: {line.quantity} | {t('warehouse.modules.consolidatedReceiving.remaining', {}, 'Gjenstår')}: {line.remainingQuantity}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-blue-600">
                        {line.remainingQuantity}
                      </div>
                      <div className="text-xs text-gray-500">
                        {line.itemUnitTxt}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
